/**
 * Festival classification and narrative building for the 🪷 section.
 *
 * Originally ported from scripts/regenerate_narratives.py (removed in B-138; superseded by the packet-based writer) — keeps the same logic
 * so that frontend-composed 🪷 sections match the pre-generated ones.
 */

import festivalDescriptions from '../../data/festivalDescriptions.json';

// ── Verb Mapping ────────────────────────────────────────────────────

const FESTIVAL_VERB_MAP: [string, string][] = [
  ['Jayanti', 'celebrated'],
  ['Jayanthi', 'celebrated'],
  ['Pūjā', 'performed'],
  ['Puja', 'performed'],
  ['Vrata', 'observed'],
  ['Ekādaśī', 'observed'],
  ['ekādaśī', 'observed'],
  ['begins', 'begins'],
  ['starts', 'starts'],
  ['Navratri', 'begins'],
  ['Saṅkrānti', 'observed'],
  ['Equinox', 'observed'],
  ['Eclipse', 'observed'],
  ['ṛtu begins', 'begins'],
  ['Pradosha', 'observed'],
  ['Shivaratri', 'observed'],
  ['Śivarātri', 'observed'],
  ['Amāvāsyā', 'observed'],
];

// ── Types ───────────────────────────────────────────────────────────

interface ClassifiedFestival {
  name: string;
  verb: string;
  calendar?: string; // "Pūrṇimānta" or "Amānta"
}

// ── Classification ──────────────────────────────────────────────────

export function classifyFestival(raw: string): ClassifiedFestival {
  let name = raw;
  let calendar: string | undefined;

  // Extract calendar prefix: (P): = Purnimanta, (A): = Amanta
  if (raw.startsWith('(P):')) {
    name = raw.slice(4).trim();
    calendar = 'Pūrṇimānta';
  } else if (raw.startsWith('(A):')) {
    name = raw.slice(4).trim();
    calendar = 'Amānta';
  }

  // Match verb by case-insensitive substring
  const nameLower = name.toLowerCase();
  for (const [pattern, verb] of FESTIVAL_VERB_MAP) {
    if (nameLower.includes(pattern.toLowerCase())) {
      return { name, verb, calendar };
    }
  }

  return { name, verb: 'celebrated', calendar };
}

// ── Opening Phrase ──────────────────────────────────────────────────

function festivalOpening(name: string, verb: string, calendar?: string): string {
  const nameLower = name.toLowerCase();

  if (verb === 'begins' || verb === 'starts') {
    if (nameLower.includes('māsa starts') || nameLower.includes('māsa begins')) {
      if (calendar) {
        return `as per the ${calendar} calendar, ${name}`;
      }
      return name;
    }
    if (nameLower.includes('begins') || nameLower.includes('starts')) {
      return name;
    }
    return `${name} ${verb}`;
  }

  return `${name} is ${verb}`;
}

// ── Build 🪷 Section ────────────────────────────────────────────────

const descriptions = festivalDescriptions as Record<string, string>;

export function buildFestivalSection(rawFestivals: string[]): string | null {
  if (!rawFestivals || rawFestivals.length === 0) return null;

  const festivals = rawFestivals
    .filter((f) => f && f.trim())
    .map(classifyFestival);

  if (festivals.length === 0) return null;

  if (festivals.length === 1) {
    const fest = festivals[0];
    const opening = festivalOpening(fest.name, fest.verb, fest.calendar);
    const desc = descriptions[fest.name];
    if (desc) {
      return `🪷 Today ${opening} - ${desc}.`;
    }
    return `🪷 Today ${opening}.`;
  }

  // Multiple festivals: group by verb for opening line
  const verbGroups = new Map<string, ClassifiedFestival[]>();
  for (const fest of festivals) {
    const group = verbGroups.get(fest.verb) || [];
    group.push(fest);
    verbGroups.set(fest.verb, group);
  }

  const openingParts: string[] = [];
  const verbKeys = Array.from(verbGroups.keys());
  for (const verb of verbKeys) {
    const fests = verbGroups.get(verb)!;
    if (verb === 'begins' || verb === 'starts') {
      for (const f of fests) {
        openingParts.push(festivalOpening(f.name, verb, f.calendar));
      }
    } else if (fests.length === 1) {
      openingParts.push(`${fests[0].name} is ${verb}`);
    } else {
      const names = fests.map((f: ClassifiedFestival) => f.name);
      const joined = names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
      openingParts.push(`${joined} are ${verb}`);
    }
  }

  const opening = '🪷 Today ' + openingParts.join(', ') + '.';

  // Build description sentences
  const descParts: string[] = [];
  for (let i = 0; i < festivals.length; i++) {
    const desc = descriptions[festivals[i].name];
    if (!desc) continue;
    if (i === 0) {
      descParts.push(`It is ${desc}`);
    } else {
      descParts.push(`${festivals[i].name} - ${desc}`);
    }
  }

  if (descParts.length > 0) {
    return opening + ' ' + descParts.join('. ') + '.';
  }
  return opening;
}
